import axios from 'axios'
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import env from '@/env'
import useIncomeStore from '@/store/income'
import { Toast } from 'antd-mobile'
import { customerTrackIncomeServe } from '@/service/arms'
export interface IncomeAxiosRequestConfig extends AxiosRequestConfig {
  ignore?: boolean
}


export interface ResponseBase<T = unknown> {
  code: number
  message: string
  data: T
  success: boolean
  timestamp: number
}

// 定义接口返回的数据结构
export interface ResponseData<T = unknown> {
  code: number
  data: T
  message: string
}

// 创建 axios 实例
const http: AxiosInstance = axios.create({
  baseURL: env.incomeBaseUrl || '/api', // 修改此处
  timeout: 60 * 1000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 从 store中获取token

    const accessToken = useIncomeStore.getState().incomeData.accessToken
    if (accessToken) {
      config.headers.accessToken = `${accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// 响应拦截器
http.interceptors.response.use(
  (response: AxiosResponse<ResponseData>) => {
    const { data, config } = response
    // 根据业务状态码处理
    if (data.code === 200) {
      return response
    }

    customerTrackIncomeServe(config.url || '', {
      data: config.data,
      params: config.params,
    }, {
      ...response.data,
    }, `BUSINESS_CODE_${data.code.toString()}`)

    // 处理登录过期
    if (data.code === 401 || data.code === 403) {
      // 清除 token
      useIncomeStore.getState().setIncomeData()
      Toast.show({
        content: '登录已过期，请重新登录',
      })
      // 跳转到登录页
      return Promise.reject(new Error('登录已过期，请重新登录'))
    }

    if (data.code === 400) {
      const _data = response.data
      if (_data?.message) {
        Toast.show({
          content: data.message || '请求失败',
        })
        return Promise.reject(new Error(data.message || '请求失败'))
      }
    }
    // 其他错误处理
    Toast.show({
      content: data.message || '请求失败',
    })
    return Promise.reject(new Error(data.message || '请求失败'))
  },
  (error) => {
    const _response = error?.response
    const { config } = _response
    
    customerTrackIncomeServe(config.url || '', {
      data: config?.data || {},
      params: config?.params || {},
    }, {
      ...(typeof _response?.data === 'object' ? _response?.data : {}),
    }, `HTTP_CODE_${_response?.status || 'ERROR'}`)

    // 网络错误处理
    // if (!window.navigator.onLine) {
    //   showToast('网络连接已断开，请检查网络设置')
    //   return Promise.reject(new Error('网络连接已断开'))
    // }

    // 超时处理
    if (error.code === 'ECONNABORTED') {
      Toast.show({
        content: '请求超时，请稍后重试',
      })
      return Promise.reject(new Error('请求超时'))
    }
    if (_response?.status === 403) {
      // 清除 token
      useIncomeStore.getState().setIncomeData()

      // 跳转到登录页
      window.location.href = '/gqs-register'
      return Promise.reject(new Error('登录已过期，请重新登录'))
    }
    if (_response?.status !== 200) {
      const _data = _response?.data
      if (_data?.message) {
        Toast.show({
          content: _data.message || '请求失败',
        })
        return Promise.reject(new Error(_data.message || '请求失败'))
      }
    }

    // 其他错误
    Toast.show({
      content: _response?.message || error.message || '请求失败',
    })
    return Promise.reject(error)
  },
)

// 封装 GET 请求
export function httpGet<T = unknown>(
  url: string,
  params?: Record<string, unknown>,
  config?: IncomeAxiosRequestConfig,
): Promise<T> {
  return http.get(url, { params, ...config }).then((response) => response.data)
}

// 封装 POST 请求
export function httpPost<T = unknown>(
  url: string,
  data?: Record<string, unknown> | FormData,
  config?: IncomeAxiosRequestConfig,
): Promise<T> {
  return http.post(url, data, config).then((response) => response.data)
}

// 封装 PUT 请求
export function httpPut<T = unknown>(
  url: string,
  data?: Record<string, unknown>,
  config?: IncomeAxiosRequestConfig,
): Promise<T> {
  return http.put(url, data, config).then((response) => response.data)
}

// 封装 DELETE 请求
export function httpDel<T = unknown>(url: string, config?: IncomeAxiosRequestConfig): Promise<T> {
  return http.delete(url, config).then((response) => response.data)
}

export default http
